import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye, EyeOff, ArrowRight, Mail, Lock, User, AlertCircle, CheckCircle2, Loader2, Users } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useStore } from '../store/useStore';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { sendWelcomeEmail, sendPasswordReset } from '../lib/email';
import { referralService } from '../lib/referral-service';

type AuthMode = 'login' | 'signup' | 'forgot' | 'mfa' | 'reset';

interface LoginProps {
  defaultMode?: AuthMode;
}

// Detect database setup errors
function isDatabaseSetupError(msg: string): boolean {
  const patterns = [
    'database error saving new user',
    'database error',
    'relation "public.profiles" does not exist',
    'relation "profiles" does not exist',
    'function public.handle_new_user',
    'could not find',
    'schema',
    '500',
  ];
  const lower = msg.toLowerCase();
  return patterns.some(p => lower.includes(p));
}

export default function Login({ defaultMode = 'login' }: LoginProps) {
  const navigate = useNavigate();
  const { login, signup, clearAuthError } = useStore();
  const [mode, setMode] = useState<AuthMode>(defaultMode);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showDbSetup, setShowDbSetup] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [partialUser, setPartialUser] = useState<any>(null);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [codeType, setCodeType] = useState<'referral' | 'team' | 'promo'>('referral');

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
    referralCode: '',
    promoCode: '',
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const handleUrlState = async () => {
      // 1. Check for URL hash (Supabase defaults)
      const hash = window.location.hash;
      if (hash.includes('type=recovery')) {
        setMode('forgot');
        return;
      }
      if (hash.includes('type=signup')) {
        setMode('signup');
        return;
      }

      // 2. Check for recovery mode
      if (window.location.hash.includes('type=recovery') || window.location.search.includes('recovery=true')) {
        setMode('forgot');
        return;
      }

      // 3. Fallback to query params
      const params = new URLSearchParams(window.location.search);
      
      // Auto-populate referral code if present
      const refCode = params.get('ref') || params.get('referral');
      if (refCode) {
        setForm(prev => ({ ...prev, referralCode: refCode }));
        setCodeType('referral');
      }

      const emailParam = params.get('email');
      const nameParam = params.get('name');
      if (emailParam || nameParam) {
        setForm(prev => ({
          ...prev,
          email: emailParam || prev.email,
          name: nameParam || prev.name,
        }));
      }

      if (params.get('signup') === 'true' || defaultMode === 'signup' || params.get('contractId')) {
        setMode('signup');
      } else if (params.get('mode') === 'forgot') {
        setMode('forgot');
      } else if (params.get('mode') === 'reset' || window.location.hash.includes('type=recovery')) {
        setMode('reset');
      }
    };

    handleUrlState();
  }, [defaultMode]);

  const switchMode = (m: AuthMode) => {
    setMode(m);
    setFieldErrors({});
    setError(null);
    setInfo(null);
    setShowDbSetup(false);
    clearAuthError();
    setForgotSent(false);
    setTermsAccepted(false);

    // Clear hashes when manually switching modes to prevent sticky URL state
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(form.email)) {
      e.email = 'Enter a valid email address';
    } else if (mode === 'signup') {
      const disposableDomains = [
        'mailinator.com',
        'yopmail.com',
        'tempmail.com',
        'temp-mail.org',
        'dispostable.com',
        'guerrillamail.com',
        'sharklasers.com',
        '10minutemail.com',
        'trashmail.com',
        'getairmail.com',
        'yopmail.fr',
        'yopmail.net',
        'cool.fr.nf',
        'jetable.org'
      ];
      const domain = form.email.split('@')[1]?.toLowerCase();
      if (domain && disposableDomains.includes(domain)) {
        e.email = 'Disposable email addresses are not allowed. Please use a business or standard personal email.';
      }
    }

    if (mode !== 'forgot') {
      if (form.password.length < 6) e.password = 'Minimum 6 characters';
    }
    if (mode === 'signup') {
      if (form.name.trim().length < 2) e.name = 'Enter your full name';
      if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
      if (!termsAccepted) e.terms = 'You must accept the Terms and Conditions';
    }
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };



  // Helper: join a team by invite code in Supabase
  const joinTeamByInviteCode = async (inviteCode: string, userId: string): Promise<{ success: boolean; teamName?: string }> => {
    if (!supabase || !inviteCode.trim()) return { success: false };
    
    try {
      // 1. Find the team by invite code
      const { data: team, error: teamErr } = await supabase
        .from('teams')
        .select('id, name')
        .eq('invite_code', inviteCode.trim())
        .single();
      
      if (teamErr || !team) {
        console.warn('Invalid invite code:', inviteCode);
        return { success: false };
      }

      // 2. Check if already a member
      const { data: existing } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', team.id)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        // Already a member — just set as preferred team
        localStorage.setItem('wholescale-preferred-team', team.id);
        return { success: true, teamName: team.name };
      }

      // 3. Insert user as a member of the invited team
      const { error: insertErr } = await supabase
        .from('team_members')
        .insert({
          team_id: team.id,
          user_id: userId,
          role: 'member',
          status: 'online',
        });

      if (insertErr) {
        console.error('Error joining team:', insertErr);
        return { success: false };
      }

      // 4. Add them to the team's general channel
      const { data: generalChannel } = await supabase
        .from('channels')
        .select('id')
        .eq('team_id', team.id)
        .eq('name', 'general')
        .maybeSingle();

      if (generalChannel) {
        await supabase
          .from('channel_members')
          .insert({
            channel_id: generalChannel.id,
            user_id: userId,
          })
          .then(() => {}); // ignore if already exists
      }

      // 5. Store preferred team so SupabaseSync loads the right one
      localStorage.setItem('wholescale-preferred-team', team.id);

      return { success: true, teamName: team.name };
    } catch (err) {
      console.error('joinTeamByInviteCode error:', err);
      return { success: false };
    }
  };

  // Helper: finalize login after successful auth
  const finalizeLogin = async (
    user: { id: string; email?: string | null; user_metadata?: Record<string, unknown> }, 
    name?: string,
    isSignup?: boolean
  ) => {
    const displayName = name || (user.user_metadata?.full_name as string) || user.email?.split('@')[0] || 'User';
    const returnTo = new URLSearchParams(window.location.search).get('return_to');

    console.log('[Auth] finalizeLogin starting for user:', user.id, 'Email:', user.email);

    // If signup had an invite code, join that team BEFORE navigating
    if (form.inviteCode.trim() && supabase) {
      const result = await joinTeamByInviteCode(form.inviteCode.trim(), user.id);
      if (result.success) {
        console.log(`[Auth] Joined team via invite code: ${result.teamName}`);
      }
    }

    // If buyer/seller signup link parameters exist, apply them now
    const params = new URLSearchParams(window.location.search);
    const roleParam = params.get('role');
    const contractIdParam = params.get('contractId');
    const agentIdParam = params.get('agentId');

    if (roleParam === 'buyer' || roleParam === 'seller') {
      console.log('[Auth] Invitation parameters found. Applying role:', roleParam);
      if (supabase) {
        try {
          await supabase.from('profiles').update({
            role: roleParam,
            onboarding_completed: false,
            settings: contractIdParam ? { invited_contract_id: contractIdParam, invited_by_agent_id: agentIdParam || null } : {}
          }).eq('id', user.id);
        } catch (err) {
          console.error('[Auth] Failed to update profile with role/contract:', err);
        }
      }
    }

    // store.login(email, password) is REDUNDANT and HARMFUL here because:
    // 1. Supabase already established the session (either via password login or MFA verification).
    // 2. Calling signInWithPassword again would reset the session to AAL1, causing an MFA loop.
    console.log('[Auth] Skipping store.login to preserve AAL session level.');
    
    const store = useStore.getState();
    const avatar = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    
    // updateProfile just sets basic info in the store
    store.updateProfile({
      id: user.id,
      email: user.email || form.email,
      name: displayName,
      avatar,
      ...(roleParam === 'buyer' || roleParam === 'seller' ? { role: roleParam, onboarding_completed: false } : {})
    });

    // Manually set authenticated in the store so routes can transition
    store.setAuthenticated(true);

    // CRITICAL: Fetch the full profile from Supabase to get user.role and other DB fields
    console.log('[Auth] Fetching full profile for:', user.id);
    await store.fetchProfile(user.id);
    sendWelcomeEmail(user.email || form.email, displayName).catch(() => {});
    
    // Redirect logic: return_to takes precedence, then onboarding for new signups, then role portals/team selection
    if (returnTo) {
      console.log('[Auth] Redirecting to returnTo:', returnTo);
      navigate(decodeURIComponent(returnTo));
    } else {
      const updatedUser = useStore.getState().currentUser;
      const isClient = updatedUser?.role === 'buyer' || updatedUser?.role === 'seller';
      const isContractor = updatedUser?.role === 'contractor';
      const isTenant = updatedUser?.role === 'tenant';

      const isInviteSignup = params.get('role') === 'buyer' || params.get('role') === 'seller' || params.get('contractId') || params.get('agentId');

      if (isSignup && !isInviteSignup) {
        localStorage.setItem('wholescale-new-signup', 'true');
        console.log('[Onboarding] New sign-up → sending to onboarding');
        navigate('/onboarding');
      } else if (isClient) {
        console.log('[Auth] Redirecting client to client-portal');
        navigate('/client-portal');
      } else if (isContractor) {
        console.log('[Auth] Redirecting contractor to contractor-portal');
        navigate(`/contractor-portal/${user.id}`);
      } else if (isTenant) {
        console.log('[Auth] Redirecting tenant to tenant-portal');
        navigate(`/tenant-portal/${user.id}`);
      } else {
        console.log('[Auth] Redirecting to team-selection');
        navigate('/team-selection');
      }
    }
  };

  const handleVerifyMfa = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!mfaFactorId || !mfaCode || !partialUser) return;
    
    setLoading(true);
    setError(null);
    try {
      if (!supabase) throw new Error('Supabase not configured');
      console.log('[Auth] MFA Challenge initiated...', { factorId: mfaFactorId, codeLength: mfaCode.length });
      
      // IMPORTANT: The TOTP code must be passed to challenge(), not verify()
      // Supabase MFA flow: challenge({ factorId, code }) -> verify({ factorId, challengeId })
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: mfaCode
      });
      
      if (challengeError) {
        console.error('[Auth] MFA Challenge/Verify Error:', challengeError);
        throw challengeError;
      }
      
      console.log('[Auth] MFA verified successfully:', challenge);
      
      // Get the updated session after MFA verification
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session?.user) {
        console.log('[Auth] Session updated after MFA, finalizing login');
        await finalizeLogin(sessionData.session.user);
      } else {
        console.log('[Auth] No session after MFA, using partial user');
        await finalizeLogin(partialUser);
      }
    } catch (err) {
      console.error('[Auth] MFA verification failed:', err);
      const msg = err instanceof Error ? err.message : 'Invalid 2FA code';
      setError(msg.includes('Invalid') ? 'Invalid code. Please check your authenticator app and try again.' : msg);
      setLoading(false);
    }
  };

  const handleCancelMfa = async () => {
    if (supabase) await supabase.auth.signOut();
    setMfaFactorId(null);
    setPartialUser(null);
    setMfaCode('');
    setMode('login');
    setError('2FA verification cancelled.');
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    setShowDbSetup(false);

    try {
      if (isSupabaseConfigured && supabase) {
        if (mode === 'login') {
          const { data, error: authError } = await supabase.auth.signInWithPassword({
            email: form.email,
            password: form.password,
          });
          
          if (authError) {
            if (isDatabaseSetupError(authError.message)) {
              setError('Database tables not set up yet.');
              setShowDbSetup(true);
            } else if (authError.message.includes('Email not confirmed') || authError.message.includes('email_not_confirmed')) {
              setError('Email not confirmed yet.');
            } else if (authError.message.includes('Invalid login') || authError.message.includes('invalid_credentials')) {
              setError('Invalid email or password.');
            } else {
              setError(authError.message);
            }
            setLoading(false);
            return;
          }

          if (data.user) {
            console.log('[Auth] User signed in, checking MFA requirements...');
            
            // 1. Check AAL
            const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            if (aalError) console.error('[Auth] MFA getAAL error:', aalError);
            console.log('[Auth] Current AAL:', aalData);

            // 2. List factors to see if any are verified
            const { data: factorData, error: factorsError } = await supabase.auth.mfa.listFactors();
            if (factorsError) console.error('[Auth] MFA listFactors error:', factorsError);
            
            // Safely extract verified factors from the response structure
            const totpFactors = factorData?.totp || [];
            const verifiedFactor = totpFactors.find((f: any) => f.status === 'verified');

            // Force MFA if we have a verified factor and the session is only AAL1
            if (verifiedFactor && aalData?.currentLevel !== 'aal2') {
              console.log('[Auth] MFA required. Verified factor ID:', verifiedFactor.id);
              setMfaFactorId(verifiedFactor.id);
              setPartialUser(data.user);
              setMfaCode(''); 
              setMode('mfa');
              setLoading(false);
              return;
            }

            
            console.log('[Auth] MFA not required or already satisfied. Finalizing login.');
            await finalizeLogin(data.user);
          }
        } else if (mode === 'signup') {

          const { data, error: authError } = await supabase.auth.signUp({
            email: form.email,
            password: form.password,
            options: {
              data: { 
                full_name: form.name.trim(),
                referral_code: referralService.generateCode(form.name.trim())
              },
            },
          });

          // Check for referral code validation
          if (form.referralCode.trim() && data.user) {
            const referrerId = await referralService.validateCode(form.referralCode);
            if (referrerId) {
              await referralService.recordReferral(referrerId, data.user.id, form.referralCode);
            }
          }

          // ─── Handle signup errors ───
          if (authError) {
            if (isDatabaseSetupError(authError.message)) {
              setError('Your Supabase database needs tables created first.');
              setShowDbSetup(true);
              setLoading(false);
              return;
            }

            setError(authError.message);
            setLoading(false);
            return;
          }

          // ─── Signup succeeded ───
          if (data.user) {
            if (data.user.identities && data.user.identities.length === 0) {
              // Duplicate user — try signing in instead
              const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                email: form.email,
                password: form.password,
              });
              if (signInData?.user && !signInError) {
                await finalizeLogin(signInData.user, form.name.trim());
                return;
              }
              setError('An account with this email already exists. Try signing in instead.');
              setLoading(false);
              return;
            }

            // If we got a session, user is auto-confirmed (email confirmation disabled)
            if (data.session) {
              await finalizeLogin(data.user, form.name.trim(), true);
              return;
            }

            // No session = needs email confirmation. Try signing in just in case.
            const { data: tryLogin, error: tryError } = await supabase.auth.signInWithPassword({
              email: form.email,
              password: form.password,
            });

            if (tryLogin?.user && !tryError) {
              await finalizeLogin(tryLogin.user, form.name.trim(), true);
              return;
            }

            setInfo(`Account created! Check ${form.email} for a verification link.`);
            setMode('login');
            setLoading(false);
            return;
          }
        } else if (mode === 'reset') {
          // Reset Password (after clicking email link)
          if (form.password !== form.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
          }
          if (form.password.length < 8) {
            setError('Password must be at least 8 characters');
            setLoading(false);
            return;
          }

          const { error: updateError } = await supabase.auth.updateUser({
            password: form.password
          });

          if (updateError) {
            setError(updateError.message);
          } else {
            toast.success('Password updated successfully! You can now sign in.');
            setMode('login');
            // Clear hash to prevent re-entering reset mode
            window.location.hash = '';
          }
          setLoading(false);
          return;
        } else {
          // Forgot password
          const resetRedirect = `${window.location.origin}/reset-password`;
          
          console.log('[Auth] Password reset process started via Resend...', {
            email: form.email,
            redirect: resetRedirect
          });
          
          const { success, error: resetError } = await sendPasswordReset(form.email, resetRedirect);
          
          if (!success) {
            setError(resetError || 'Failed to send reset email.');
          } else {
            console.log('[Auth] Password reset email dispatched successfully to:', form.email);
            setForgotSent(true);
          }
          setLoading(false);
          return;
        }
      } else {
        // Demo mode — always works
        if (mode === 'login') {
          login(form.email, form.password);
          navigate('/');
        } else if (mode === 'signup') {
          signup(form.name.trim(), form.email, form.password);
          navigate('/');
        } else {
          setForgotSent(true);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      if (isDatabaseSetupError(msg)) {
        setError('Database tables need to be created.');
        setShowDbSetup(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full pl-10 pr-4 py-2.5 text-xs rounded-2xl border transition-all outline-none focus:ring-4 focus:ring-indigo-500/20';
  const inputStyle = {
    background: 'rgba(255, 255, 255, 0.03)',
    borderColor: 'rgba(255, 255, 255, 0.07)',
    color: 'white',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
  };

  return (
    <div className="min-h-[100dvh] flex bg-black overflow-hidden font-inter">
      {/* Left: Brand Panel (Cinematic) */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ 
          background: 'radial-gradient(circle at top left, rgba(99, 102, 241, 0.15), transparent), radial-gradient(circle at bottom right, rgba(168, 85, 247, 0.1), transparent)'
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] pointer-events-none" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzYjgyZjYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE4YzEuNjU3IDAgMyAxLjM0MyAzIDN2MThsLTMgM0gxOGwtMy0zVjIxYzAtMS42NTcgMS4zNDMtMyAzLTNoMTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />

        <div className="relative z-10 ">
          <div className="flex items-center gap-4 mb-6">
            <Logo size={48} />
            <div>
              <h1 className="text-3xl font-black tracking-tighter text-white">WholeScale</h1>
              <p className="text-xs uppercase tracking-[0.4em] font-black italic text-indigo-400">OPERATING SYSTEM</p>
            </div>
          </div>
          <p className="text-lg leading-relaxed max-w-sm font-medium text-slate-400">
            The elite real estate ecosystem for high-performance wholesalers.
          </p>
        </div>

        <div className="relative z-10 space-y-6 ">
          {[
            { title: 'Nexus Lead Engine', desc: 'AI-powered deal scoring & neural routing', color: 'bg-indigo-500/20', iconColor: 'text-indigo-400' },
            { title: 'Global Sync Hub', desc: 'Real-time multi-channel communication', color: 'bg-purple-500/20', iconColor: 'text-purple-400' },
            { title: 'Tactical Maps', desc: 'Interactive geographic intelligence layering', color: 'bg-blue-500/20', iconColor: 'text-blue-400' }
          ].map((feature, i) => (
            <div key={i} className="flex items-center gap-5 p-5 rounded-3xl border border-white/5 bg-white/[0.03] backdrop-blur-md hover:bg-white/[0.05] transition-all hover:translate-x-2 group">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${feature.color} group-hover:scale-110 transition-transform`}>
                <CheckCircle2 size={24} className={feature.iconColor} />
              </div>
              <div>
                <p className="text-sm font-bold text-white tracking-wide">{feature.title}</p>
                <p className="text-xs text-slate-500">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="relative">
          <p className="text-xs opacity-40" style={{ color: 'var(--t-text)' }}>© 2026 WholeScale OS. All rights reserved.</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-4 overflow-y-auto bg-black scrollbar-hide py-4 lg:py-2">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[20%] right-[10%] w-64 h-64 bg-indigo-500/5 blur-[100px] rounded-full" />
          <div className="absolute bottom-[20%] left-[10%] w-64 h-64 bg-purple-500/5 blur-[100px] rounded-full" />
        </div>

        <div className="w-full max-w-md relative z-10 ">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-3 lg:hidden">
            <Logo size={24} showText={true} />
          </div>

          <div className="mb-1.5 text-center lg:text-left">
            <h2 className="text-lg sm:text-xl font-black tracking-tight text-white mb-0">
              {mode === 'login' ? 'Mission Control' : 
               mode === 'signup' ? 'Initiate Sequence' : 
               mode === 'mfa' ? 'Neural Shield' :
               mode === 'forgot' ? 'Override Access' :
               mode === 'reset' ? 'Protocol Reset' :
               'Welcome Back'}
            </h2>
            <p className="text-slate-400 font-medium text-xs">
              {mode === 'login'
                ? 'Authorized personnel only. Authenticate to proceed.'
                : mode === 'signup'
                ? 'Establish your tactical operational profile.'
                : mode === 'mfa'
                ? 'Secondary verification required for secure uplink.'
                : mode === 'reset'
                ? 'Update your secure encryption parameters.'
                : 'Requesting emergency recovery credentials.'}
            </p>
          </div>

          {/* Regular error */}
          {error && !showDbSetup && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-xl border" style={{ 
              backgroundColor: 'var(--t-error-dim)',
              borderColor: 'var(--t-error-border)'
            }}>
              <AlertCircle size={16} className="shrink-0" style={{ color: 'var(--t-error)' }} />
              <p className="text-sm" style={{ color: 'var(--t-error)' }}>{error}</p>
            </div>
          )}

          {info && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-xl border"
              style={{ background: 'var(--t-primary-dim)', borderColor: 'var(--t-primary-dim)' }}
            >
              <CheckCircle2 size={16} className="shrink-0" style={{ color: 'var(--t-primary)' }} />
              <p className="text-sm" style={{ color: 'var(--t-primary)' }}>{info}</p>
            </div>
          )}

          {forgotSent && (
            <div className="p-3 mb-4 rounded-xl border space-y-2" style={{ 
              backgroundColor: 'var(--t-success-dim)',
              borderColor: 'var(--t-success-border)'
            }}>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="shrink-0" style={{ color: 'var(--t-success)' }} />
                <p className="text-sm font-bold" style={{ color: 'var(--t-success)' }}>Password reset link sent!</p>
              </div>
              <p className="text-xs ml-6" style={{ color: 'var(--t-success)', opacity: 0.9 }}>
                Check your email for the reset link. <strong>Don't forget to check your spam folder</strong> if you don't see it within a few minutes.
              </p>
            </div>
          )}

          {mode === 'mfa' ? (
            <form onSubmit={handleVerifyMfa} className="space-y-4">
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'var(--t-text-muted)' }}>Authenticator Code</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-text-muted)' }} />
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className={inputClass}
                    style={{ ...inputStyle, textAlign: 'center', letterSpacing: '0.25em' }}
                    disabled={loading}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelMfa}
                  className="flex-1 py-3 text-sm font-semibold rounded-xl transition-all border"
                  style={{ background: 'var(--t-surface)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || mfaCode.length < 6}
                  className="flex-[2] flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--t-primary)', color: 'var(--t-on-primary, white)' }}
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" />Verifying...</> : 'Verify & Sign In'}
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Name (signup only) */}
            {mode === 'signup' && (
                <div className="space-y-3">
                <div>
                  <label className="text-[10px] mb-1 block font-bold tracking-tight text-slate-400 uppercase">Full Name</label>
                  <div className="relative group">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-indigo-400 transition-colors" style={{ color: 'var(--t-text-muted)' }} />
                    <input
                      value={form.name}
                      onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Enter your name"
                      className={inputClass}
                      style={inputStyle}
                      disabled={loading}
                      required
                    />
                  </div>
                  {fieldErrors.name && <p className="text-xs mt-1" style={{ color: 'var(--t-error)' }}>{fieldErrors.name}</p>}
                </div>

                {/* Code Type Selector */}
                <div className="flex p-1 bg-white/[0.03] rounded-2xl border border-white/5 backdrop-blur-sm">
                  {(['referral', 'team', 'promo'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setCodeType(type)}
                      className={`flex-1 py-1.5 text-[9px] font-black uppercase tracking-[0.1em] rounded-xl transition-all ${
                        codeType === type 
                          ? 'bg-indigo-500/20 text-indigo-400 shadow-[0_0_20px_rgba(99,102,241,0.15)] border border-indigo-500/20' 
                          : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {type === 'referral' ? 'Revenue' : type === 'team' ? 'Invite' : 'Promo'}
                    </button>
                  ))}
                </div>

                <div className="">
                  {codeType === 'referral' && (
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1.5 block">Revenue Share Code</label>
                      <div className="relative group">
                        <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                          type="text"
                          placeholder="AGENT-X"
                          value={form.referralCode}
                          onChange={(e) => setForm({ ...form, referralCode: e.target.value.toUpperCase() })}
                          className={inputClass}
                          style={inputStyle}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}

                  {codeType === 'team' && (
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1.5 block">Team Invite Code</label>
                      <div className="relative group">
                        <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                          type="text"
                          placeholder="WS-XXXXXX"
                          value={form.inviteCode}
                          onChange={(e) => setForm({ ...form, inviteCode: e.target.value.toUpperCase() })}
                          className={inputClass}
                          style={inputStyle}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}

                  {codeType === 'promo' && (
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1 mb-1.5 block">Promo Code</label>
                      <div className="relative group">
                        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                          type="text"
                          placeholder="SAVE50"
                          value={form.promoCode}
                          onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase() })}
                          className={inputClass}
                          style={inputStyle}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="text-[10px] mb-1 block font-bold tracking-tight text-slate-400 uppercase">Email Address</label>
              <div className="relative group">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-indigo-400 transition-colors" style={{ color: 'var(--t-text-muted)' }} />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="name@example.com"
                  className={inputClass}
                  style={inputStyle}
                  disabled={loading}
                  required
                />
              </div>
              {fieldErrors.email && <p className="text-xs mt-1" style={{ color: 'var(--t-error)' }}>{fieldErrors.email}</p>}
            </div>

            {/* Password */}
            {mode !== 'forgot' && (
              <div>
                <label className="text-[10px] mb-1 block font-bold tracking-tight text-slate-400 uppercase">
                  {mode === 'reset' ? 'New Secure Password' : 'Password'}
                </label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-indigo-400 transition-colors" style={{ color: 'var(--t-text-muted)' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="••••••••"
                    className={`${inputClass} pr-12`}
                    style={inputStyle}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:text-white transition-colors"
                    style={{ color: 'var(--t-text-muted)' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && <p className="text-xs mt-1" style={{ color: 'var(--t-error)' }}>{fieldErrors.password}</p>}
              </div>
            )}

            {/* Confirm password (signup or reset) */}
            {(mode === 'signup' || mode === 'reset') && (
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'var(--t-text-muted)' }}>Confirm Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-text-muted)' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                    placeholder="••••••••"
                    className={inputClass}
                    style={inputStyle}
                    disabled={loading}
                  />
                </div>
                {fieldErrors.confirmPassword && <p className="text-xs mt-1" style={{ color: 'var(--t-error)' }}>{fieldErrors.confirmPassword}</p>}
              </div>
            )}


            {/* Terms and Conditions (signup only) */}
            {mode === 'signup' && (
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/5 transition-all hover:bg-white/[0.05]">
                <div className="relative flex items-center pt-0.5">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--t-border)] bg-[var(--t-bg)] text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0 transition-all cursor-pointer accent-indigo-500"
                    required
                  />
                </div>
                <label htmlFor="terms" className="text-xs leading-relaxed text-slate-400 cursor-pointer select-none">
                  I have read and agree to the <a href="/terms" target="_blank" className="text-indigo-400 font-bold hover:underline">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-indigo-400 font-bold hover:underline">Privacy Protocol</a>.
                </label>
              </div>
            )}

            {/* Forgot password link */}
            {mode === 'login' && (
              <div className="flex justify-end">
                <button 
                  type="button" 
                  onClick={() => switchMode('forgot')} 
                  className="text-xs hover:opacity-80 transition-opacity"
                  style={{ color: 'var(--t-primary)' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || (mode === 'signup' && !termsAccepted)}
              className="w-full h-11 flex items-center justify-center gap-3 text-xs font-black uppercase tracking-widest italic rounded-2xl transition-all shadow-[0_0_30px_rgba(99,102,241,0.2)] hover:shadow-[0_0_40px_rgba(99,102,241,0.4)] disabled:opacity-30 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
              style={{ 
                background: 'linear-gradient(135deg, var(--t-primary), #818cf8)',
                color: 'white',
              }}
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" />Processing...</>
              ) : (
                <>{mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : mode === 'reset' ? 'Update Password' : 'Send Reset Link'}<ArrowRight size={16} /></>
              )}
            </button>
          </form>
          )}



          {/* Mode switcher */}
          <p className="mt-4 text-center text-sm" style={{ color: 'var(--t-text-muted)' }}>
              {mode === 'login' ? (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    className="font-black text-white hover:text-indigo-400 transition-colors uppercase tracking-widest text-xs ml-1"
                  >
                    Initiate Signup
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    className="font-bold hover:underline"
                    style={{ color: 'var(--t-primary)' }}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
        </div>
      </div>
    </div>
  );
}

